// Video query keys with hierarchical structure
export const videoKeys = {
  // Base key for all video-related queries
  all: ['videos'] as const,
  
  // List queries
  lists: () => [...videoKeys.all, 'list'] as const,
  list: (filters?: VideoFilters) => [...videoKeys.lists(), { filters }] as const,
  userVideos: (userId: string, filters?: VideoFilters) => [
    ...videoKeys.lists(), 
    'user', 
    userId, 
    { filters }
  ] as const,
  // Separate keys for admin (all pinned) vs user (user-specific pinned)
  pinnedVideos: (isAdmin: boolean = false) => [...videoKeys.lists(), 'pinned', isAdmin ? 'admin' : 'user'] as const,
  
  // Detail queries
  details: () => [...videoKeys.all, 'detail'] as const,
  detail: (id: string) => [...videoKeys.details(), id] as const,
  
  // Status queries
  statuses: () => [...videoKeys.all, 'status'] as const,
  statusCounts: () => [...videoKeys.statuses(), 'counts'] as const,
  userStatusCounts: (userId: string) => [...videoKeys.statuses(), 'user', userId] as const,
  
  // Transcription queries
  transcriptions: () => [...videoKeys.all, 'transcription'] as const,
  transcription: (videoId: string) => [...videoKeys.transcriptions(), videoId] as const,
  transcriptionStatus: (videoId: string) => [
    ...videoKeys.transcriptions(), 
    videoId, 
    'status'
  ] as const,
  
  // Generation queries
  generations: () => [...videoKeys.all, 'generation'] as const,
  generation: (id: string) => [...videoKeys.generations(), id] as const,
  generationProgress: (id: string) => [...videoKeys.generations(), id, 'progress'] as const,
  userGenerations: (userId: string) => [...videoKeys.generations(), 'user', userId] as const,
  
  // Infinite queries
  infinite: (filters?: VideoFilters) => [...videoKeys.lists(), 'infinite', { filters }] as const,
  infiniteUser: (userId: string, filters?: VideoFilters) => [
    ...videoKeys.lists(), 
    'infinite', 
    'user', 
    userId, 
    { filters }
  ] as const,
  infinitePinned: () => [...videoKeys.lists(), 'infinite', 'pinned'] as const,
  
  // Admin queries
  admin: () => [...videoKeys.all, 'admin'] as const,
  adminAll: (page?: number, status?: string) => [
    ...videoKeys.admin(), 
    'all', 
    { page, status }
  ] as const,
  adminCounts: () => [...videoKeys.admin(), 'counts'] as const,
  adminGeneration: () => [...videoKeys.admin(), 'generation'] as const,
  
  // Favourite queries
  favourites: (limit?: number, offset?: number) => [
    ...videoKeys.all, 
    'favourites', 
    { limit, offset }
  ] as const,
  
  // Archive queries (admin only)
  archives: (limit?: number, offset?: number) => [
    ...videoKeys.all, 
    'archives', 
    { limit, offset }
  ] as const,

  // Extension request queries
  extensionRequests: () => [...videoKeys.all, 'extension-requests'] as const,
  extensionRequestsInfinite: (filters?: { userEmail?: string; status?: string }) => [
    ...videoKeys.extensionRequests(),
    'infinite',
    { filters },
  ] as const,
  myExtensionRequestsInfinite: (filters?: { status?: string }) => [
    ...videoKeys.extensionRequests(),
    'my',
    'infinite',
    { filters },
  ] as const,
} as const

// Video filters interface
export interface VideoFilters {
  status?: 'pending' | 'approved' | 'rejected' | 'processing' | 'generating'
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'duration'
  sortOrder?: 'asc' | 'desc'
  search?: string
  dateFrom?: string
  dateTo?: string
  duration?: 'short' | 'medium' | 'long' // < 30s, 30s-5m, > 5m
  pinned?: boolean
  limit?: number
  page?: number
}

// Video generation status type
export type VideoGenerationStatus = 
  | 'queued' 
  | 'initializing' 
  | 'processing' 
  | 'rendering' 
  | 'finalizing' 
  | 'completed' 
  | 'failed'

// Type for all possible video query keys
export type VideoQueryKey = typeof videoKeys[keyof typeof videoKeys]

// Utility functions for video keys
export const videoKeyUtils = {
  // Check if a key is for user videos
  isUserVideoKey: (key: readonly unknown[]): boolean => {
    return key.includes('user') && key.includes('videos')
  },
  
  // Extract user ID from user video key
  getUserIdFromKey: (key: readonly unknown[]): string | null => {
    const userIndex = key.indexOf('user')
    if (userIndex !== -1 && userIndex + 1 < key.length) {
      const userId = key[userIndex + 1]
      return typeof userId === 'string' ? userId : null
    }
    return null
  },
  
  // Check if key is for video generation
  isGenerationKey: (key: readonly unknown[]): boolean => {
    return key.includes('generation')
  },
  
  // Check if key is for transcription
  isTranscriptionKey: (key: readonly unknown[]): boolean => {
    return key.includes('transcription')
  },
  
  // Extract video ID from transcription/generation key
  getVideoIdFromKey: (key: readonly unknown[]): string | null => {
    const transcriptionIndex = key.indexOf('transcription')
    const generationIndex = key.indexOf('generation')
    
    if (transcriptionIndex !== -1 && transcriptionIndex + 1 < key.length) {
      const videoId = key[transcriptionIndex + 1]
      return typeof videoId === 'string' ? videoId : null
    }
    
    if (generationIndex !== -1 && generationIndex + 1 < key.length) {
      const generationId = key[generationIndex + 1]
      return typeof generationId === 'string' ? generationId : null
    }
    
    return null
  },
  
  // Check if key matches a pattern
  matchesPattern: (key: readonly unknown[], pattern: readonly unknown[]): boolean => {
    if (pattern.length > key.length) return false
    
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] !== key[i]) return false
    }
    
    return true
  },
}

export default videoKeys